<template>
  <div style="margin:20px">
    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px; position: relative;">
      <span style="font-size: 26px; font-weight: bold">Driver Scorecard</span><br/>
      <span style="font-style: italic; font-size: 18px">Individual driver performance metrics and KPIs from Samsara Safety, Samsara Idling, TBN Dispatch Pricing, and TBN Driver Availability</span>
      <router-link v-if="user.role == 'admin' || user.role == 'customer-admin' || user.role == 'tbn-admin'" :to="{ name: 'tenant_config_data_services' }" title="Scorecard Settings" style="position: absolute; top: 6px; right: 10px; color: #555; font-size: 20px;">
        <i class="fa fa-cog"></i>
      </router-link>
    </div>

    <div style="font-size: 20px; font-weight: bold;" class="align-right d-flex align-items-center justify-content-center">
      <a href="#" class="white-link" v-on:click.prevent="shift_date_range(-1)"><</a>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_fromDate" :config="optionsFrom" :placeholder="details_fromDate"/>
      &nbsp;to&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="details_toDate" :config="optionsTo" :placeholder="details_toDate"/>
      &nbsp;<a href="#" class="white-link" v-on:click.prevent="shift_date_range(1)">></a>
      &nbsp;
      <span v-if="includes_current_month" class="badge badge-warning" style="font-size:0.75rem; vertical-align:middle;" title="Selected range includes the current month, which is computed on-the-fly and may be slower">⚡ On-the-fly</span>
      <span v-else class="badge badge-success" style="font-size:0.75rem; vertical-align:middle;" title="All months in this range are pre-calculated">✓ Pre-calc</span>
      &nbsp;&nbsp;|&nbsp;&nbsp;
      <span>Status:</span>&nbsp;
      <b-form-select v-model="selected_status_filter" :options="status_filter_options" style="font-size: 16px; width:160px"/>
      &nbsp;&nbsp;|&nbsp;&nbsp;
      <span>Driver:</span>&nbsp;
      <b-form-select v-if="driver_options.length > 0" v-model="selected_driver_id" :options="filtered_driver_options" style="font-size: 18px; font-weight: bold; width:220px"/>
      <span v-else><b-spinner style="width: 2rem; height: 2rem;" label="Loading..."></b-spinner></span>
      &nbsp;&nbsp;|&nbsp;&nbsp;
      <b-button v-on:click.prevent="load_scorecard()" :disabled="loading || !selected_driver_id">Load Scorecard</b-button>
      &nbsp;&nbsp;
      <b-button size="sm" variant="outline-success" v-on:click.prevent="download_all_csv()" :disabled="downloading_csv">{{ downloading_csv ? 'Preparing...' : 'Download All as CSV' }}</b-button>
    </div>

    <div v-if="from_before_dvir_cutoff" class="text-center text-muted mb-2" style="font-size: 0.85rem;">
      ⚠️ DVIR data is only available for the last 90 days — on-the-fly calculations before {{ dvir_cutoff_label }} will not include DVIR scores.
    </div>

    <div v-if="loading" style="text-align: center; margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <div v-else-if="scorecard_data" style="margin: 20px;">
      <b-card>
        <div class="d-flex align-items-center mb-2" style="position: relative;">
          <div class="d-flex flex-wrap align-items-center justify-content-center gap-3 flex-grow-1">
            <div>
              <h3 class="mb-0">{{ scorecard_data.driver_name || 'Driver Scorecard' }}</h3>
              <div class="d-flex align-items-center mt-1" style="gap: 8px; flex-wrap: wrap;">
                <span v-if="selected_driver_info && selected_driver_info.employmentStatus" class="badge badge-secondary" style="font-size: 0.85rem;">{{ selected_driver_info.employmentStatus }}</span>
                <span v-if="scorecard_data.date_employment_start" class="text-muted" style="font-size: 0.9rem;">
                  Hired {{ formatHireDate(scorecard_data.date_employment_start) }}<span v-if="tenureString(scorecard_data.date_employment_start)"> &bull; {{ tenureString(scorecard_data.date_employment_start) }}</span>
                </span>
              </div>
            </div>
            <div class="ml-4">
              <h5 class="mb-0">Miles Driven</h5>
              <h3 class="mb-0">{{ formatNumber(scorecard_data.miles_driven, 2) || formatNumber(scorecard_data.distance_traveled_miles, 2) || '0.00' }}</h3>
              <span v-if="showDelta('miles_driven', deltas.miles_driven)" class="scorecard-delta delta-neutral">{{ deltas.miles_driven.dir }} {{ deltas.miles_driven.pct }}%</span>
            </div>
          </div>
          <b-button variant="outline-primary" size="sm" @click="generatePdfReport()" :disabled="generating_pdf" class="d-none d-md-block" style="position: absolute; right: 0; white-space: nowrap;">
            {{ generating_pdf ? 'Preparing PDF…' : '&#11123; Download PDF Report' }}
          </b-button>
        </div>
        <p>
          {{ details_fromDate }} to {{ details_toDate }} ({{ scorecard_data.date_range_days || 0 }} days)<span v-if="scorecard_data.current_month_included" class="text-muted ml-2">— Current month computed on-the-fly; last 5 days excluded.</span>
          <span v-if="previous_period" class="text-muted ml-3" style="font-size:0.85rem;">vs. {{ previous_period.comparison_start_month === previous_period.comparison_end_month ? previous_period.comparison_start_month : previous_period.comparison_start_month + ' – ' + previous_period.comparison_end_month }}</span>
        </p>

        <hr/>
        
        <div v-if="scorecard_data.compiled_score !== undefined && scorecard_data.compiled_score !== null" class="compiled-score-section mb-3">
          <h4 class="text-center">Compiled Performance Score</h4>
          <div class="d-flex align-items-center justify-content-center flex-wrap gap-3">
            <div class="compiled-score-badge" :class="getCompiledScoreClass(scorecard_data.compiled_score)">
              <span class="compiled-score-number">{{ scorecard_data.compiled_score }}</span>
              <span class="compiled-score-label">/ 100</span>
            </div>
            <div class="compiled-score-tier-badge" :class="getCompiledTierBadgeClass(getCompiledScoreTier(scorecard_data.compiled_score))">
              {{ getCompiledScoreTier(scorecard_data.compiled_score) }}
            </div>
          </div>
          <div class="text-center mt-2" v-if="scorecard_data.compiled_score_breakdown && scorecard_data.compiled_score_breakdown.length">
            <a href="#" class="compiled-weights-toggle" @click.prevent="show_compiled_weights = !show_compiled_weights">
              {{ show_compiled_weights ? '▲ Hide weights' : '▼ Show weights' }}
            </a>
            <div v-if="show_compiled_weights" class="compiled-weights-list mt-2">
              <span
                v-for="item in scorecard_data.compiled_score_breakdown"
                :key="item.metric"
                class="compiled-weight-chip"
              >{{ formatMetricLabel(item.metric) }}: {{ item.weight }} pts</span>
            </div>
          </div>
          <hr/>
        </div>

        <h4 class="scorecard-section-header">Risk Metrics</h4>
        <div class="scorecard-metrics-row">
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Risk Score</h5>
            <h2 :class="getRiskScoreClass(scorecard_data.risk_score)">
              {{ scorecard_data.risk_score || 'N/A' }}
            </h2>
            <span v-if="showDelta('risk_score', deltas.risk_score)" class="scorecard-delta" :class="deltas.risk_score.neutral ? 'delta-neutral' : deltas.risk_score.improved ? 'delta-good' : 'delta-bad'">{{ deltas.risk_score.dir }} {{ deltas.risk_score.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Risk Rank</h5>
            <h2 :class="getRiskRankClass(scorecard_data.risk_score_rank)">
              {{ scorecard_data.risk_score_rank || 'N/A' }}
            </h2>
            <span v-if="showDelta('risk_rank', deltas.risk_score_rank)" class="scorecard-delta" :class="deltas.risk_score_rank.neutral ? 'delta-neutral' : deltas.risk_score_rank.improved ? 'delta-good' : 'delta-bad'">{{ deltas.risk_score_rank.dir }} {{ deltas.risk_score_rank.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Speeding %</h5>
            <h3 :class="fieldClass('speeding_percentage', scorecard_data.speeding_percentage)">{{ formatNumber(scorecard_data.speeding_percentage, 2) || 0 }}%</h3>
            <span v-if="showDelta('speeding_percentage', deltas.speeding_percentage)" class="scorecard-delta" :class="deltas.speeding_percentage.neutral ? 'delta-neutral' : deltas.speeding_percentage.improved ? 'delta-good' : 'delta-bad'">{{ deltas.speeding_percentage.dir }} {{ deltas.speeding_percentage.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Distracted Driving per 10k</h5>
            <h3 :class="fieldClass('distracted_driving_percentage', scorecard_data.distracted_driving_percentage)">{{ formatNumber(scorecard_data.distracted_driving_percentage, 2) || 0 }}</h3>
            <span v-if="showDelta('distracted_driving_percentage', deltas.distracted_driving_percentage)" class="scorecard-delta" :class="deltas.distracted_driving_percentage.neutral ? 'delta-neutral' : deltas.distracted_driving_percentage.improved ? 'delta-good' : 'delta-bad'">{{ deltas.distracted_driving_percentage.dir }} {{ deltas.distracted_driving_percentage.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Following Dist. per 10k</h5>
            <h3 :class="fieldClass('following_distance_per_10k_miles', scorecard_data.following_distance_per_10k_miles)">{{ formatNumber(scorecard_data.following_distance_per_10k_miles, 2) || 0 }}</h3>
            <span v-if="showDelta('following_distance_per_10k_miles', deltas.following_distance_per_10k_miles)" class="scorecard-delta" :class="deltas.following_distance_per_10k_miles.neutral ? 'delta-neutral' : deltas.following_distance_per_10k_miles.improved ? 'delta-good' : 'delta-bad'">{{ deltas.following_distance_per_10k_miles.dir }} {{ deltas.following_distance_per_10k_miles.pct }}%</span>
          </b-card>
        </div>

        <hr/>

        <h4 class="scorecard-section-header">Idling &amp; Fuel Efficiency</h4>
        <div class="scorecard-metrics-row">
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Idling %</h5>
            <h3 :class="getIdlingClass(scorecard_data.idling_percentage)">
              {{ formatNumber(scorecard_data.idling_percentage, 2) }}%
            </h3>
            <span v-if="showDelta('idling_percentage', deltas.idling_percentage)" class="scorecard-delta" :class="deltas.idling_percentage.neutral ? 'delta-neutral' : deltas.idling_percentage.improved ? 'delta-good' : 'delta-bad'">{{ deltas.idling_percentage.dir }} {{ deltas.idling_percentage.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Idling Rank</h5>
            <h2 :class="getIdlingRankClass(scorecard_data.idling_rank)">
              {{ scorecard_data.idling_rank || 'N/A' }}
            </h2>
            <span v-if="showDelta('idling_rank', deltas.idling_rank)" class="scorecard-delta" :class="deltas.idling_rank.neutral ? 'delta-neutral' : deltas.idling_rank.improved ? 'delta-good' : 'delta-bad'">{{ deltas.idling_rank.dir }} {{ deltas.idling_rank.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>MPG</h5>
            <h3 :class="getMpgClass(scorecard_data.miles_per_gallon)">{{ formatNumber(scorecard_data.miles_per_gallon, 2) || 'N/A' }}</h3>
            <span v-if="showDelta('miles_per_gallon', deltas.miles_per_gallon)" class="scorecard-delta" :class="deltas.miles_per_gallon.neutral ? 'delta-neutral' : deltas.miles_per_gallon.improved ? 'delta-good' : 'delta-bad'">{{ deltas.miles_per_gallon.dir }} {{ deltas.miles_per_gallon.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Idle Hrs</h5>
            <h3 :class="fieldClass('idle_hours', scorecard_data.idle_hours)">{{ formatNumber(scorecard_data.idle_hours, 2) }}</h3>
            <span v-if="showDelta('idle_hours', deltas.idle_hours)" class="scorecard-delta delta-neutral">{{ deltas.idle_hours.dir }} {{ deltas.idle_hours.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Run Hrs</h5>
            <h3>{{ formatNumber(scorecard_data.run_hours, 2) }}</h3>
            <span v-if="showDelta('run_hours', deltas.run_hours)" class="scorecard-delta delta-neutral">{{ deltas.run_hours.dir }} {{ deltas.run_hours.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Gallons</h5>
            <h3 :class="fieldClass('fuel_consumed_gallons', scorecard_data.fuel_consumed_gallons)">{{ formatNumber(scorecard_data.fuel_consumed_gallons, 2) || '0.00' }}</h3>
            <span v-if="showDelta('fuel_consumed_gallons', deltas.fuel_consumed_gallons)" class="scorecard-delta delta-neutral">{{ deltas.fuel_consumed_gallons.dir }} {{ deltas.fuel_consumed_gallons.pct }}%</span>
          </b-card>
        </div>

        <hr/>

        <h4 class="scorecard-section-header">Dispatch (from TBN)</h4>
        <div class="scorecard-metrics-row">
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Customer Requests</h5>
            <h3 :class="getCustomerRequestsClass(scorecard_data.customer_requests || 0)">{{ scorecard_data.customer_requests || 0 }}</h3>
            <span v-if="showDelta('customer_requests', deltas.customer_requests)" class="scorecard-delta" :class="deltas.customer_requests.neutral ? 'delta-neutral' : deltas.customer_requests.improved ? 'delta-good' : 'delta-bad'">{{ deltas.customer_requests.dir }} {{ deltas.customer_requests.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Cust. Req. per 10k</h5>
            <h3 :class="getCustomerRequestsPer10kClass(scorecard_data.customer_requests_per_10k_miles || 0)">{{ formatNumber(scorecard_data.customer_requests_per_10k_miles, 2) || 0 }}</h3>
            <span v-if="showDelta('customer_requests_per_10k_miles', deltas.customer_requests_per_10k_miles)" class="scorecard-delta" :class="deltas.customer_requests_per_10k_miles.improved ? 'delta-good' : 'delta-bad'">{{ deltas.customer_requests_per_10k_miles.dir }} {{ deltas.customer_requests_per_10k_miles.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Bus Days</h5>
            <h3 :class="fieldClass('bus_days', scorecard_data.bus_days)">{{ scorecard_data.bus_days || 0 }}</h3>
            <span v-if="showDelta('bus_days', deltas.bus_days)" class="scorecard-delta delta-neutral">{{ deltas.bus_days.dir }} {{ deltas.bus_days.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Dispatch Miles</h5>
            <h3>{{ formatNumber(scorecard_data.total_miles, 2) }}</h3>
            <span v-if="showDelta('total_miles', deltas.total_miles)" class="scorecard-delta delta-neutral">{{ deltas.total_miles.dir }} {{ deltas.total_miles.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Weekend Avail.</h5>
            <h3 :class="fieldClass('weekend_availability_percentage', scorecard_data.weekend_availability_percentage)">{{ formatNumber(scorecard_data.weekend_availability_percentage, 2) || 0 }}%</h3>
            <span v-if="showDelta('weekend_availability_percentage', deltas.weekend_availability_percentage)" class="scorecard-delta" :class="deltas.weekend_availability_percentage.neutral ? 'delta-neutral' : deltas.weekend_availability_percentage.improved ? 'delta-good' : 'delta-bad'">{{ deltas.weekend_availability_percentage.dir }} {{ deltas.weekend_availability_percentage.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Dispatch Count</h5>
            <h3>{{ scorecard_data.dispatch_count || 0 }}</h3>
            <span v-if="showDelta('dispatch_count', deltas.dispatch_count)" class="scorecard-delta delta-neutral">{{ deltas.dispatch_count.dir }} {{ deltas.dispatch_count.pct }}%</span>
          </b-card>
        </div>

        <hr/>

        <h4 class="scorecard-section-header">DVIR (Vehicle Inspections)</h4>
        <div class="scorecard-metrics-row">
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Completion Rate</h5>
            <h3 :class="getDvirCompletionRateClass(scorecard_data.dvir_completion_rate)">
              {{ scorecard_data.dvir_completion_rate !== null && scorecard_data.dvir_completion_rate !== undefined ? formatNumber(scorecard_data.dvir_completion_rate, 1) + '%' : 'N/A' }}
            </h3>
            <span v-if="showDelta('dvir_completion_rate', deltas.dvir_completion_rate)" class="scorecard-delta" :class="deltas.dvir_completion_rate.neutral ? 'delta-neutral' : deltas.dvir_completion_rate.improved ? 'delta-good' : 'delta-bad'">{{ deltas.dvir_completion_rate.dir }} {{ deltas.dvir_completion_rate.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Pre-Trip DVIRs</h5>
            <h3>{{ scorecard_data.pre_trip_dvir_count || 0 }}</h3>
            <span v-if="showDelta('pre_trip_dvir_count', deltas.pre_trip_dvir_count)" class="scorecard-delta delta-neutral">{{ deltas.pre_trip_dvir_count.dir }} {{ deltas.pre_trip_dvir_count.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Post-Trip DVIRs</h5>
            <h3>{{ scorecard_data.post_trip_dvir_count || 0 }}</h3>
            <span v-if="showDelta('post_trip_dvir_count', deltas.post_trip_dvir_count)" class="scorecard-delta delta-neutral">{{ deltas.post_trip_dvir_count.dir }} {{ deltas.post_trip_dvir_count.pct }}%</span>
          </b-card>
          <b-card bg-variant="light" class="metric-card metric-card-inline">
            <h5>Total DVIRs</h5>
            <h3>{{ scorecard_data.total_dvir_count || 0 }}</h3>
            <span v-if="showDelta('total_dvir_count', deltas.total_dvir_count)" class="scorecard-delta delta-neutral">{{ deltas.total_dvir_count.dir }} {{ deltas.total_dvir_count.pct }}%</span>
          </b-card>
        </div>
      </b-card>
    </div>

    <div v-else-if="!loading && selected_driver_id" style="text-align: center; margin: 50px; color: #666;">
      <p>No scorecard data available. Click "Load Scorecard" to fetch data.</p>
    </div>

    <!-- Hidden PDF export area -->
    <div style="position: absolute; overflow: hidden; height: 0; width: 0;">
      <vue-html2pdf
        :enable-download="true"
        :manual-pagination="true"
        pdf-content-width="750px"
        :html-to-pdf-options="htmlToPdfOptions"
        ref="scorecardPdf"
      >
        <section v-if="pdf_scorecard_data" slot="pdf-content" class="pdf-item" style="font-family: Arial, sans-serif; color: #1a1a2e; padding: 0 10px;">

          <!-- PDF Header -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: #1a3a6b; border-radius: 4px;">
            <div style="display: flex; align-items: center; gap: 10px;">
              <img src="/favicon-white.png" style="height: 36px; object-fit: contain;" alt="Fleet Integrations"/>
              <span style="font-size: 16px; font-weight: 700; color: #fff;">Fleet Integrations</span>
            </div>
            <div style="font-size: 18px; font-weight: 700; color: #fff;">Driver Scorecard Report</div>
          </div>

          <!-- Driver + period summary -->
          <div style="margin: 8px 0 8px 0; padding: 8px 12px; background: #f0f4fb; border-left: 5px solid #1a3a6b; border-radius: 3px; text-align: center;">
            <div style="font-size: 16px; font-weight: 700; color: #1a3a6b; margin-bottom: 2px;">{{ pdf_scorecard_data.driver_name || 'Driver' }}</div>
            <div v-if="selected_driver_info && (selected_driver_info.employmentStatus || scorecard_data.date_employment_start)" style="font-size: 12px; color: #444; margin-bottom: 2px;">
              <span v-if="selected_driver_info && selected_driver_info.employmentStatus"><strong>Status:</strong> {{ selected_driver_info.employmentStatus }}</span>
              <span v-if="selected_driver_info && selected_driver_info.employmentStatus && scorecard_data.date_employment_start">&nbsp;&nbsp;|&nbsp;&nbsp;</span>
              <span v-if="scorecard_data.date_employment_start"><strong>Hire Date:</strong> {{ formatHireDate(scorecard_data.date_employment_start) }}<span v-if="tenureString(scorecard_data.date_employment_start)"> ({{ tenureString(scorecard_data.date_employment_start) }})</span></span>
            </div>
            <div style="font-size: 12px; color: #444;">
              {{ details_fromDate }} &mdash; {{ details_toDate }}
              ({{ pdf_scorecard_data.date_range_days || 0 }} days)
              &nbsp;&nbsp;<strong>Miles Driven:</strong> {{ formatNumber(pdf_scorecard_data.miles_driven || pdf_scorecard_data.distance_traveled_miles, 2) }}
            </div>
            <div v-if="pdf_scorecard_data.current_month_included" style="font-size: 10px; color: #888; margin-top: 2px; font-style: italic;">
              Current month data calculated on-the-fly; last 5 days excluded.
            </div>
          </div>

          <!-- Risk Metrics section -->
          <div style="margin-top: 8px;">
            <div style="background: #1a3a6b; color: #fff; font-size: 11px; font-weight: 700; padding: 4px 10px; letter-spacing: 0.4px; text-transform: uppercase;">
              Risk Metrics
            </div>
            <table style="width: 100%; border-collapse: collapse; background: #fff;">
              <thead>
                <tr style="background: #e8edf5;">
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Risk Score</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Risk Rank</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Speeding %</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Distracted Driving per 10k</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Following Dist. per 10k</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 18px; font-weight: 700;" :style="{ color: getRiskScoreColor(pdf_scorecard_data.risk_score) }">{{ pdf_scorecard_data.risk_score || 'N/A' }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 18px; font-weight: 700;" :style="{ color: getRiskRankColor(pdf_scorecard_data.risk_score_rank) }">{{ pdf_scorecard_data.risk_score_rank || 'N/A' }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ formatNumber(pdf_scorecard_data.speeding_percentage, 2) || 0 }}%</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ formatNumber(pdf_scorecard_data.distracted_driving_percentage, 2) || 0 }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ formatNumber(pdf_scorecard_data.following_distance_per_10k_miles, 2) || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Idling & Fuel section -->
          <div style="margin-top: 8px;">
            <div style="background: #1a3a6b; color: #fff; font-size: 11px; font-weight: 700; padding: 4px 10px; letter-spacing: 0.4px; text-transform: uppercase;">
              Idling &amp; Fuel Efficiency
            </div>
            <table style="width: 100%; border-collapse: collapse; background: #fff;">
              <thead>
                <tr style="background: #e8edf5;">
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Idling %</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Idling Rank</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">MPG</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Idle Hours</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Run Hours</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Fuel (Gallons)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 700;" :style="{ color: getIdlingColor(pdf_scorecard_data.idling_percentage) }">{{ formatNumber(pdf_scorecard_data.idling_percentage, 2) }}%</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 18px; font-weight: 700;" :style="{ color: getIdlingRankColor(pdf_scorecard_data.idling_rank) }">{{ pdf_scorecard_data.idling_rank || 'N/A' }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 700;" :style="{ color: getMpgColor(pdf_scorecard_data.miles_per_gallon) }">{{ formatNumber(pdf_scorecard_data.miles_per_gallon, 2) || 'N/A' }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ formatNumber(pdf_scorecard_data.idle_hours, 2) }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ formatNumber(pdf_scorecard_data.run_hours, 2) }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ formatNumber(pdf_scorecard_data.fuel_consumed_gallons, 2) || '0.00' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Dispatch section -->
          <div style="margin-top: 8px;">
            <div style="background: #1a3a6b; color: #fff; font-size: 11px; font-weight: 700; padding: 4px 10px; letter-spacing: 0.4px; text-transform: uppercase;">
              Dispatch (from TBN)
            </div>
            <table style="width: 100%; border-collapse: collapse; background: #fff;">
              <thead>
                <tr style="background: #e8edf5;">
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Customer Requests</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Cust. Req. per 10k</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Bus Days</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Dispatch Miles</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Weekend Avail.</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Dispatch Count</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 700;" :style="{ color: (pdf_scorecard_data.customer_requests || 0) === 0 ? '#d97706' : '#16a34a' }">{{ pdf_scorecard_data.customer_requests || 0 }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 700;" :style="{ color: (pdf_scorecard_data.customer_requests_per_10k_miles || 0) === 0 ? '#d97706' : '#16a34a' }">{{ formatNumber(pdf_scorecard_data.customer_requests_per_10k_miles, 2) || 0 }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ pdf_scorecard_data.bus_days || 0 }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ formatNumber(pdf_scorecard_data.total_miles, 2) }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ formatNumber(pdf_scorecard_data.weekend_availability_percentage, 2) || 0 }}%</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ pdf_scorecard_data.dispatch_count || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- DVIR section -->
          <div style="margin-top: 8px;">
            <div style="background: #1a3a6b; color: #fff; font-size: 11px; font-weight: 700; padding: 4px 10px; letter-spacing: 0.4px; text-transform: uppercase;">
              DVIR (Vehicle Inspections)
            </div>
            <table style="width: 100%; border-collapse: collapse; background: #fff;">
              <thead>
                <tr style="background: #e8edf5;">
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Pre-Trip DVIRs</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Post-Trip DVIRs</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Total DVIRs</th>
                  <th style="padding: 4px 8px; font-size: 10px; font-weight: 600; text-align: center; border: 1px solid #d0d8e8;">Completion Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ pdf_scorecard_data.pre_trip_dvir_count || 0 }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ pdf_scorecard_data.post_trip_dvir_count || 0 }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 16px; font-weight: 600; color: #1a1a2e;">{{ pdf_scorecard_data.total_dvir_count || 0 }}</td>
                  <td style="padding: 8px; text-align: center; border: 1px solid #d0d8e8; font-size: 18px; font-weight: 700;" :style="{ color: getDvirCompletionRateColor(pdf_scorecard_data.dvir_completion_rate) }">{{ formatNumber(pdf_scorecard_data.dvir_completion_rate, 1) }}%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Color legend + footer -->
          <div style="margin-top: 10px; padding: 6px 12px; background: #f8f9fa; border: 1px solid #e0e0e0; border-radius: 3px; display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 10px; font-weight: 600; color: #555;">
              <span style="color: #16a34a;">&#9632;</span> Good &nbsp;
              <span style="color: #d97706;">&#9632;</span> Needs Attention &nbsp;
              <span style="color: #dc2626;">&#9632;</span> Poor
            </span>
            <span style="font-size: 10px; color: #888;">Fleet Integrations &mdash; Generated: {{ pdfGeneratedAt }}</span>
          </div>

        </section>
      </vue-html2pdf>
    </div>
  </div>
</template>

<script>
import {mapState} from "vuex"
import {justDate} from '../helpers/dates'
import moment from 'moment'
import axios from 'axios'
import {tbn_api_url, get_driver_scorecard, tbn_get_drivers_scorecard_list, tbn_clear_scorecard_driver_list_cache, check_driver_scorecard_precalc} from '../networking/tbn_apis'
import { mergeThresholds, getFieldClass, getCompiledScoreClass } from '../utils/scorecard_thresholds'
import VueHtml2pdf from 'vue-html2pdf'

export default {
  name: 'driver_scorecard',

  components: { VueHtml2pdf },

  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    mergedThresholds() {
      return mergeThresholds(this.scorecard_thresholds)
    },
    deltas() {
      if (!this.previous_period || !this.scorecard_data) return {}
      const calc = (field, higherIsBetter = true, neutral = false) => {
        const cur = Number(this.scorecard_data[field])
        const prev = Number(this.previous_period[field])
        if (!isFinite(cur) || !isFinite(prev)) return null
        if (prev === 0) return { pct: '0.0', improved: true, dir: '▲', neutral: true }
        const pct = ((cur - prev) / Math.abs(prev)) * 100
        const isZero = Math.abs(pct) < 0.05
        const improved = higherIsBetter ? pct >= 0 : pct <= 0
        return { pct: Math.abs(pct).toFixed(1), improved, dir: pct >= 0 ? '▲' : '▼', neutral: neutral || isZero }
      }
      const info = (field) => calc(field, true, true)
      return {
        miles_driven:                       info('miles_driven'),
        risk_score:                         calc('risk_score', true),
        speeding_percentage:                calc('speeding_percentage', false),
        distracted_driving_percentage:      calc('distracted_driving_percentage', false),
        following_distance_per_10k_miles:   calc('following_distance_per_10k_miles', false),
        idling_percentage:                  calc('idling_percentage', false),
        idling_rank:                        calc('idling_rank', false),
        miles_per_gallon:                   calc('miles_per_gallon', true),
        risk_score_rank:                    calc('risk_score_rank', false),
        idle_hours:                         info('idle_hours'),
        run_hours:                          info('run_hours'),
        fuel_consumed_gallons:              info('fuel_consumed_gallons'),
        bus_days:                           info('bus_days'),
        total_miles:                        info('total_miles'),
        weekend_availability_percentage:    calc('weekend_availability_percentage', true),
        customer_requests:                  calc('customer_requests', true),
        customer_requests_per_10k_miles:    calc('customer_requests_per_10k_miles', true),
        dispatch_count:                     info('dispatch_count'),
        pre_trip_dvir_count:                info('pre_trip_dvir_count'),
        post_trip_dvir_count:               info('post_trip_dvir_count'),
        total_dvir_count:                   info('total_dvir_count'),
        dvir_completion_rate:               calc('dvir_completion_rate', true),
      }
    },
    htmlToPdfOptions() {
      return {
        margin: [12, 10, 12, 10],
        filename: this.pdfFilename + '.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        enableLinks: false,
        // scale: 2 renders at double resolution for sharper text/borders in the PDF
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
      }
    },
    includes_current_month() {
      const current = moment().format('YYYY-MM')
      const startM = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM')
      const endM = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM')
      const dateRangeIncludesCurrent = startM <= current && endM >= current
      if (dateRangeIncludesCurrent) return true
      // If we've checked the backend, use the real answer.
      if (this.is_precalculated !== null) return !this.is_precalculated
      // No check yet (no driver selected) — default to Pre-calc for past ranges.
      return false
    },
    from_before_dvir_cutoff() {
      const cutoff = moment().subtract(90, 'days').startOf('day')
      return moment(this.details_fromDate, 'MM-DD-YYYY').isBefore(cutoff)
    },
    dvir_cutoff_label() {
      return moment().subtract(90, 'days').format('MMM D, YYYY')
    },
    selected_driver_info() {
      if (!this.selected_driver_id) return null
      return this.all_drivers.find(d => String(d.value) === String(this.selected_driver_id)) || null
    },
    status_filter_options() {
      const statuses = [...new Set(
        this.all_drivers
          .map(d => d.employmentStatus)
          .filter(s => s && s.trim())
      )].sort()
      return [
        { value: null, text: 'All Statuses' },
        ...statuses.map(s => ({ value: s, text: s })),
      ]
    },
    filtered_driver_options() {
      if (!this.selected_status_filter) return this.driver_options
      const allowed = new Set(
        this.all_drivers
          .filter(d => d.employmentStatus === this.selected_status_filter)
          .map(d => String(d.value))
      )
      return this.driver_options.filter(o => o.value === null || allowed.has(String(o.value)))
    },
    /** Picker bounds + only 1st of each month selectable (bootstrap-datetimepicker enabledDates) */
    optionsFrom() {
      const minD = this.scorecard_start_date
        ? moment(this.scorecard_start_date).startOf('month')
        : moment().subtract(3, 'years').startOf('month')
      const maxD = moment()
      return {
        format: 'MM-DD-YYYY',
        useCurrent: false,
        widgetParent: '#scorecard-dtpicker-portal',
        widgetPositioning: { horizontal: 'left', vertical: 'bottom' },
        minDate: minD,
        maxDate: maxD,
        enabledDates: this.buildFirstOfMonthDates(minD, maxD),
        keepOpen: false,
        showClear: true,
        showClose: true,
      }
    },
    /** Last day of each month, or today when ending a partial current month */
    optionsTo() {
      const minD = this.scorecard_start_date
        ? moment(this.scorecard_start_date).startOf('month')
        : moment().subtract(3, 'years').startOf('month')
      const maxD = moment()
      return {
        format: 'MM-DD-YYYY',
        useCurrent: false,
        widgetParent: '#scorecard-dtpicker-portal',
        widgetPositioning: { horizontal: 'left', vertical: 'bottom' },
        minDate: minD,
        maxDate: maxD,
        enabledDates: this.buildLastOfMonthOrTodayDates(minD, maxD),
        keepOpen: false,
        showClear: true,
        showClose: true,
      }
    },
  },

  data() {
    return {
      loading: false,
      pdfFilename: '',
      pdfGeneratedAt: '',
      generating_pdf: false,
      pdf_scorecard_data: null,
      pdf_previous_period: null,
      downloading_csv: false,
      scorecard_data: null,
      previous_period: null,
      selected_driver_id: null,
      is_precalculated: null,
      driver_options: [],
      all_drivers: [],
      selected_status_filter: null,
      total_drivers: 0,
      scorecard_thresholds: null,
      scorecard_start_date: null,
      show_compiled_weights: false,
      details_fromDate: justDate((moment().date() >= 7 ? moment().subtract(1, 'month') : moment().subtract(2, 'months')).startOf('month')),
      details_toDate: justDate((moment().date() >= 7 ? moment().subtract(1, 'month') : moment().subtract(2, 'months')).endOf('month')),
    }
  },

  created() {
    this._ensureDtPickerPortal()
    this.load_drivers()
  },

  beforeDestroy() {
    this._removeDtPickerPortal()
  },

  watch: {
    details_toDate() {
      const today = moment()
      if (moment(this.details_toDate, 'MM-DD-YYYY').isAfter(today))
        this.details_toDate = justDate(today)

      if (moment(this.details_fromDate, 'MM-DD-YYYY') > moment(this.details_toDate, 'MM-DD-YYYY'))
        this.details_fromDate = justDate(moment(this.details_toDate, 'MM-DD-YYYY').startOf('month'))

      const months = moment(this.details_toDate, 'MM-DD-YYYY').diff(moment(this.details_fromDate, 'MM-DD-YYYY'), 'months', true)
      if (months > 12) {
        this.details_fromDate = moment(this.details_toDate, 'MM-DD-YYYY').subtract(12, 'months').startOf('month').format('MM-DD-YYYY')
      }
      this.refresh_precalc_badge()
    },
    details_fromDate() {
      if (moment(this.details_fromDate, 'MM-DD-YYYY') > moment(this.details_toDate, 'MM-DD-YYYY'))
        this.details_toDate = justDate(moment(this.details_fromDate, 'MM-DD-YYYY').endOf('month'))

      const today = moment()
      if (moment(this.details_toDate, 'MM-DD-YYYY').isAfter(today))
        this.details_toDate = justDate(today)

      const months = moment(this.details_toDate, 'MM-DD-YYYY').diff(moment(this.details_fromDate, 'MM-DD-YYYY'), 'months', true)
      if (months > 12) {
        this.details_toDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(12, 'months').endOf('month').format('MM-DD-YYYY')
      }
      this.refresh_precalc_badge()
    },
    selected_driver_id() {
      this.refresh_precalc_badge()
    },
  },

  methods: {
    /** Create a positioned portal div in <body> for the datepicker calendar.
     *  Placed outside #app so #app's overflow-x:hidden (→ overflow-y:auto)
     *  scroll context cannot clip the absolutely-positioned calendar widget. */
    _ensureDtPickerPortal() {
      if (document.getElementById('scorecard-dtpicker-portal')) return
      const el = document.createElement('div')
      el.id = 'scorecard-dtpicker-portal'
      Object.assign(el.style, {
        position: 'absolute', top: '0', left: '0',
        width: '0', height: '0', overflow: 'visible', zIndex: '9999',
      })
      document.body.appendChild(el)
    },
    _removeDtPickerPortal() {
      const el = document.getElementById('scorecard-dtpicker-portal')
      if (el) el.remove()
    },

    async refresh_precalc_badge() {
      if (!this.details_fromDate || !this.details_toDate) {
        this.is_precalculated = null
        return
      }
      const start_month = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM')
      const end_month = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM')
      const current = moment().format('YYYY-MM')
      if (start_month <= current && end_month >= current) {
        this.is_precalculated = false
        return
      }
      // Use the selected driver, or fall back to the first available driver as a probe.
      // If precalc ran it ran for all drivers, so any one of them gives the right answer.
      const probe_id = this.selected_driver_id
        || (this.all_drivers && this.all_drivers[0] && this.all_drivers[0].value)
      if (!probe_id) {
        this.is_precalculated = null
        return
      }
      try {
        const response = await check_driver_scorecard_precalc(this.user.tenant, probe_id, start_month, end_month)
        const data = (response && response.value && response.value.data) || (response && response.data)
        if (data && !data.error) {
          this.is_precalculated = data.is_precalculated
        }
      } catch (e) {
        this.is_precalculated = null
      }
    },
    /** First calendar day of each month between minD and maxD (inclusive) */
    buildFirstOfMonthDates(minD, maxD) {
      const out = []
      let m = minD.clone().startOf('month')
      const lastMonthStart = maxD.clone().startOf('month')
      while (m.isSameOrBefore(lastMonthStart, 'month')) {
        const first = m.clone().date(1)
        if (first.isSameOrBefore(maxD, 'day') && first.isSameOrAfter(minD, 'day')) {
          out.push(first.clone())
        }
        m.add(1, 'month')
      }
      return out
    },
    /** Last day of each month in range; also "today" for the current month when today is not month-end */
    buildLastOfMonthOrTodayDates(minD, maxD) {
      const out = []
      const today = moment().startOf('day')
      let m = minD.clone().startOf('month')
      const lastMonthStart = maxD.clone().startOf('month')
      while (m.isSameOrBefore(lastMonthStart, 'month')) {
        const last = m.clone().endOf('month').startOf('day')
        if (last.isSameOrAfter(minD, 'day') && last.isSameOrBefore(maxD, 'day')) {
          out.push(last.clone())
        }
        if (m.isSame(today, 'month') && today.isBefore(last, 'day')) {
          out.push(today.clone())
        }
        m.add(1, 'month')
      }
      return out
    },

    async load_drivers(skipCacheBust = false) {
      try {
        const response = await tbn_get_drivers_scorecard_list(this.user.tenant)
        const {value: {data}} = response
        let drivers = []
        if (Array.isArray(data)) {
          drivers = data
        } else if (data && data.data && Array.isArray(data.data)) {
          drivers = data.data
        }
        const filteredDrivers = drivers.filter(driver => driver.value && driver.value !== '')
        // Auto-bust stale cache if employmentStatus or dateEmploymentStart is missing (one-time, on first load)
        if (!skipCacheBust && filteredDrivers.length > 0 && (!filteredDrivers.some(d => d.employmentStatus) || !filteredDrivers.some(d => d.dateEmploymentStart))) {
          try { await tbn_clear_scorecard_driver_list_cache(this.user.tenant) } catch (_) {}
          return this.load_drivers(true)
        }
        const sorted = [...filteredDrivers].sort((a, b) => {
          const textA = (a.text || a.fullName || a.name || '').toLowerCase()
          const textB = (b.text || b.fullName || b.name || '').toLowerCase()
          return textA.localeCompare(textB)
        })
        this.all_drivers = sorted
        this.total_drivers = sorted.length
        this.refresh_precalc_badge()
        this.driver_options = [
          { value: null, text: 'Select Driver' },
          ...sorted.map(driver => ({
            value: driver.value,
            text: driver.text || driver.fullName || driver.name || `${driver.firstName || ''} ${driver.lastName || ''}`.trim()
          }))
        ]
      } catch (error) {
        console.error('Error loading drivers:', error)
        this.driver_options = [{ value: null, text: 'Select Driver' }]
      }
    },

    shift_date_range(shift) {
      const newFrom = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'month')
      let newTo = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'month').endOf('month')
      const today = moment()
      if (newTo.isAfter(today)) newTo = today.clone()
      if (newFrom.isAfter(today)) return
      this.details_fromDate = newFrom.format('MM-DD-YYYY')
      this.details_toDate = newTo.format('MM-DD-YYYY')
    },

    async load_scorecard() {
      if (!this.selected_driver_id) return

      this.loading = true
      this.scorecard_data = null
      this.show_compiled_weights = false
      this.previous_period = null

      try {
        const start_month = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM')
        const end_month = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM')

        const response = await get_driver_scorecard(
          this.user.tenant,
          this.selected_driver_id,
          start_month,
          end_month
        )

        const responseData = (response && response.value && response.value.data) || (response && response.data)

        if (responseData) {
          if (responseData.error) {
            this.$bvToast.toast(responseData.error, {
              title: 'Scorecard Error', variant: 'warning', solid: true, autoHideDelay: 8000
            })
          } else if (responseData.results) {
            this.scorecard_data = responseData.results
            this.scorecard_thresholds = responseData.scorecard_thresholds !== undefined ? responseData.scorecard_thresholds : null
            this.previous_period = responseData.previous_period || null
            if (responseData.scorecard_start_date && !this.scorecard_start_date) {
              this.scorecard_start_date = responseData.scorecard_start_date
            }
          } else {
            this.$bvToast.toast('No scorecard data available for this driver and date range', {
              title: 'No Data', variant: 'warning', solid: true
            })
          }
        } else {
          this.$bvToast.toast('Unexpected response format from server', {
            title: 'Error', variant: 'danger', solid: true
          })
        }
      } catch (error) {
        console.error('Error loading scorecard:', error)
        const serverMsg = (error.response && error.response.data && error.response.data.error)
            || (error.response && error.response.data && error.response.data.data && error.response.data.data.error)
        this.$bvToast.toast(serverMsg || error.message || 'Error loading scorecard data', {
          title: 'Error', variant: 'danger', solid: true, autoHideDelay: 8000
        })
      } finally {
        this.loading = false
      }
    },

    async download_all_csv() {
      const start_month = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM')
      const end_month = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM')
      this.downloading_csv = true
      const statusNote = this.selected_status_filter ? ` (${this.selected_status_filter} only)` : ''
      this.$bvToast.toast(`Preparing CSV${statusNote} — current month is excluded from the export.`, { variant: 'info', autoHideDelay: 5000 })
      try {
        const statusParam = this.selected_status_filter ? `&status_filter=${encodeURIComponent(this.selected_status_filter)}` : ''
        const response = await axios.get(
          `${tbn_api_url}tbn/driver-scorecard/all/csv?tenant=${this.user.tenant}&start_month=${start_month}&end_month=${end_month}${statusParam}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` }, responseType: 'blob' }
        )
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        const fileStatus = this.selected_status_filter ? `-${this.selected_status_filter.replace(/\s+/g, '_')}` : ''
        link.setAttribute('download', `driver-scorecard${fileStatus}-${start_month}-to-${end_month}.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        this.$bvToast.toast('Download complete.', { variant: 'success', autoHideDelay: 3000 })
      } catch (e) {
        this.$bvToast.toast('Could not download scorecard data.', { variant: 'danger' })
      } finally {
        this.downloading_csv = false
      }
    },

    formatNumber(value, decimals = 2) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toFixed(decimals)
    },

    formatHireDate(dateStr) {
      if (!dateStr) return ''
      const m = moment(dateStr, ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss'], true)
      if (m.isValid()) return m.format('MMM D, YYYY')
      const mFallback = moment(dateStr)
      return mFallback.isValid() ? mFallback.format('MMM D, YYYY') : dateStr
    },

    tenureString(dateStr) {
      if (!dateStr) return ''
      let start = moment(dateStr, ['MM-DD-YYYY', 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss'], true)
      if (!start.isValid()) start = moment(dateStr)
      if (!start.isValid()) return ''
      const now = moment()
      const years = now.diff(start, 'years')
      const months = now.diff(start.clone().add(years, 'years'), 'months')
      if (years === 0 && months === 0) return 'Less than 1 month'
      const parts = []
      if (years > 0) parts.push(`${years} yr${years !== 1 ? 's' : ''}`)
      if (months > 0) parts.push(`${months} mo`)
      return parts.join(', ')
    },

    getRiskScoreClass(riskScore) {
      return getFieldClass('risk_score', riskScore, this.mergedThresholds)
    },

    getRiskRankClass(riskRank) {
      if (this.total_drivers === 0) return ''
      return getFieldClass('risk_rank', riskRank, this.mergedThresholds, { total_drivers: this.total_drivers })
    },

    getIdlingClass(idlingPercentage) {
      return getFieldClass('idling_percentage', idlingPercentage, this.mergedThresholds)
    },

    getIdlingRankClass(idlingRank) {
      if (this.total_drivers === 0) return ''
      return getFieldClass('idling_rank', idlingRank, this.mergedThresholds, { total_drivers: this.total_drivers })
    },

    getMpgClass(mpg) {
      if (mpg === null || mpg === undefined) return 'text-danger'
      return getFieldClass('miles_per_gallon', mpg, this.mergedThresholds)
    },

    getDvirCompletionRateClass(rate) {
      return getFieldClass('dvir_completion_rate', rate, this.mergedThresholds)
    },

    getCustomerRequestsClass(value) {
      return getFieldClass('customer_requests', value, this.mergedThresholds)
    },

    getCustomerRequestsPer10kClass(value) {
      return getFieldClass('customer_requests_per_10k_miles', value, this.mergedThresholds)
    },

    fieldClass(key, value) {
      return getFieldClass(key, value, this.mergedThresholds)
    },

    showDelta(thresholdKey, delta) {
      return !!delta && !!this.mergedThresholds[thresholdKey]
    },

    getRiskScoreColor(riskScore) {
      if (riskScore === null || riskScore === undefined) return '#1a1a2e'
      const score = Number(riskScore)
      if (score >= 80) return '#16a34a'
      if (score >= 50) return '#d97706'
      return '#dc2626'
    },
    getRiskRankColor(riskRank) {
      if (riskRank === null || riskRank === undefined || this.total_drivers === 0) return '#1a1a2e'
      const percentile = (Number(riskRank) / this.total_drivers) * 100
      if (percentile <= 25) return '#16a34a'
      if (percentile <= 75) return '#d97706'
      return '#dc2626'
    },
    getIdlingRankColor(idlingRank) {
      if (idlingRank === null || idlingRank === undefined || this.total_drivers === 0) return '#1a1a2e'
      const percentile = (Number(idlingRank) / this.total_drivers) * 100
      if (percentile <= 25) return '#16a34a'
      if (percentile <= 75) return '#d97706'
      return '#dc2626'
    },
    getIdlingColor(pct) {
      if (pct === null || pct === undefined) return '#1a1a2e'
      const v = Number(pct)
      if (v < 21.8) return '#16a34a'
      if (v <= 30) return '#d97706'
      return '#dc2626'
    },
    getMpgColor(mpg) {
      if (mpg === null || mpg === undefined) return '#dc2626'
      const v = Number(mpg)
      if (v >= 7.1) return '#16a34a'
      if (v >= 6.6) return '#d97706'
      return '#dc2626'
    },
    getDvirCompletionRateColor(rate) {
      if (rate === null || rate === undefined) return '#1a1a2e'
      const v = Number(rate)
      if (v >= 80) return '#16a34a'
      if (v >= 50) return '#d97706'
      return '#dc2626'
    },

    async generatePdfReport() {
      if (!this.selected_driver_id) return
      this.generating_pdf = true
      this.$bvToast.toast('Fetching fresh data for PDF report…', { variant: 'info', autoHideDelay: 4000 })
      try {
        const start_month = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM')
        const end_month = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM')
        const response = await get_driver_scorecard(
          this.user.tenant,
          this.selected_driver_id,
          start_month,
          end_month
        )
        const responseData = (response && response.value && response.value.data) || (response && response.data)
        if (!responseData || !responseData.results) {
          this.$bvToast.toast('Could not fetch fresh scorecard data for PDF.', { variant: 'danger' })
          return
        }
        this.pdf_scorecard_data = responseData.results
        this.pdf_previous_period = responseData.previous_period || null
        const name = this.pdf_scorecard_data.driver_name || 'Driver'
        this.pdfFilename = `${name} ${this.details_fromDate} to ${this.details_toDate}`
        this.pdfGeneratedAt = moment().format('MMMM D, YYYY [at] h:mm A')
        await this.$nextTick()
        if (this.$refs.scorecardPdf) this.$refs.scorecardPdf.generatePdf()
      } catch (e) {
        this.$bvToast.toast('Error fetching PDF data: ' + (e.message || 'Unknown error'), { variant: 'danger' })
      } finally {
        this.generating_pdf = false
      }
    },

    getCompiledScoreClass(score) {
      return getCompiledScoreClass(score, this.mergedThresholds.compiled_score)
    },

    getCompiledScoreTier(score) {
      if (score === null || score === undefined) return ''
      const cfg = this.mergedThresholds.compiled_score
      const s = Number(score)
      if (cfg) {
        if (s >= Number(cfg.elite))    return 'Elite'
        if (s >= Number(cfg.solid))    return 'Solid'
        if (s >= Number(cfg.coaching)) return 'Needs Coaching'
        return 'High Risk'
      }
      // No compiled_score threshold configured (tenant explicitly removed it) —
      // fall back to the tier string supplied by the backend so the badge still renders.
      return this.scorecard_data ? this.scorecard_data.compiled_score_tier : ''
    },

    getCompiledTierBadgeClass(tier) {
      const map = {
        'Elite': 'badge-tier-elite',
        'Solid': 'badge-tier-solid',
        'Needs Coaching': 'badge-tier-coaching',
        'High Risk': 'badge-tier-highrisk',
      }
      return map[tier] || ''
    },

    formatMetricLabel(metric) {
      const labels = {
        risk_score: 'Risk Score',
        speeding_percentage: 'Speeding %',
        idling_percentage: 'Idling %',
        following_distance_per_10k_miles: 'Following Dist.',
        distracted_driving_percentage: 'Distracted / 10k',
        weekend_availability_percentage: 'Wknd Avail.',
        customer_requests_per_10k_miles: 'Cust. Req. / 10k',
        dvir_completion_rate: 'DVIR Rate',
      }
      return labels[metric] || metric
    }
  }
}
</script>

<style scoped>
.white-link {
  color: white;
  text-decoration: none;
  font-size: 24px;
}
.white-link:hover {
  color: #ccc;
}

.scorecard-section-header {
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #1a3a6b;
  margin-bottom: 0.75rem;
}

.scorecard-metrics-row {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 1.1rem;
  margin-bottom: 1.35rem;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;
  -webkit-overflow-scrolling: touch;
}

.compiled-score-section {
  padding: 4px 0;
}

.compiled-score-badge {
  display: inline-flex;
  align-items: baseline;
  padding: 8px 18px;
  border-radius: 8px;
  font-weight: bold;
  border: 2px solid transparent;
}
.compiled-score-number {
  font-size: 2.6rem;
  line-height: 1;
}
.compiled-score-label {
  font-size: 1rem;
  margin-left: 4px;
  opacity: 0.7;
}
.compiled-score-elite   { background: #d4edda; border-color: #28a745; color: #155724; }
.compiled-score-solid   { background: #cce5ff; border-color: #004085; color: #004085; }
.compiled-score-coaching { background: #fff3cd; border-color: #856404; color: #856404; }
.compiled-score-highrisk { background: #f8d7da; border-color: #721c24; color: #721c24; }

.compiled-score-tier-badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}
.badge-tier-elite    { background: #28a745; color: #fff; }
.badge-tier-solid    { background: #007bff; color: #fff; }
.badge-tier-coaching { background: #ffc107; color: #212529; }
.badge-tier-highrisk { background: #dc3545; color: #fff; }

.compiled-weights-toggle {
  font-size: 13px;
  color: #888;
  text-decoration: none;
}
.compiled-weights-toggle:hover { color: #555; }
.compiled-weights-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
}
.compiled-weight-chip {
  background: #f1f3f5;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 13px;
  color: #555;
}
.metric-card-inline {
  flex: 0 0 auto;
  min-width: 195px;
  padding: 1.1rem 1.25rem !important;
  margin-bottom: 0 !important;
}
.metric-card-inline h5 {
  font-size: 1.1rem;
  margin-bottom: 0.4rem;
  line-height: 1.2;
}
.metric-card-inline h2 {
  font-size: 1.85rem;
}
.metric-card-inline h3 {
  font-size: 1.6rem;
}
.scorecard-delta {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-top: 3px;
  letter-spacing: 0.01em;
}
.delta-good {
  color: #28a745;
}
.delta-bad {
  color: #dc3545;
}
.delta-neutral {
  color: #6c757d;
}
</style>
